import { Component } from '@theme/component';

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (this.closest('.blog-post-content')) this.#normalizeBlogTypography();
    this.#removeEmptyParagraphs();
    this.querySelectorAll('table').forEach(this.#formatTable);
    this.#groupConsecutiveImages();
  }

  /**
   * Word and other rich text editors paste presentational styles into the
   * article HTML. Those inline declarations override the blog's typography
   * even when two posts use the same article template. Keep semantic markup
   * (headings, emphasis, links, lists) and media sizing, but let the theme
   * control text size, colour, alignment, and paragraph spacing.
   */
  #normalizeBlogTypography() {
    const textSelector = 'p, h1, h2, h3, h4, h5, h6, li, span, font, div, blockquote, a, ul, ol, strong, b, em, i, u, th, td';
    const typography = [
      'font-family', 'font-size', 'line-height', 'letter-spacing',
      'word-spacing', 'color', 'text-align', 'text-indent',
    ];
    const spacing = [
      'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
      'margin-block', 'margin-block-start', 'margin-block-end',
    ];

    this.querySelectorAll(textSelector).forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      let properties = typography;
      if (element.matches('p, h1, h2, h3, h4, h5, h6, li, ul, ol')) {
        properties = [...typography, ...spacing, 'background', 'background-color'];
      } else if (element.matches('span, font, strong, b, em, i, u, th, td')) {
        properties = [...typography, 'background', 'background-color'];
      }
      properties.forEach((property) => element.style.removeProperty(property));

      if (element.tagName === 'FONT') {
        element.removeAttribute('face');
        element.removeAttribute('size');
        element.removeAttribute('color');
      }
      if (!element.style.length) element.removeAttribute('style');
    });
  }

  /**
   * Content migrated from other platforms (e.g. WordPress) often leaves
   * behind blank paragraphs, sometimes wrapped in spans or containing a
   * lone break. These take up a full line plus a paragraph margin.
   */
  #removeEmptyParagraphs() {
    const blog = this.closest('.blog-post-content');
    const blank = (element) => !element.textContent.replace(/[\s\u200b\u2060\ufeff\u00ad]/g, '').length;
    const meaningfulChild = 'a, img, picture, iframe, video, audio, svg, table, input, button, hr';
    this.querySelectorAll('p').forEach((p) => {
      if (blank(p) && (!p.children.length || (blog && !p.querySelector(meaningfulChild)))) {
        p.remove();
        return;
      }

      if (!blog || p.closest('table')) return;
      const whitespaceNode = (node) => node.nodeType === Node.TEXT_NODE
        && !node.textContent.replace(/[\s\u200b\u2060\ufeff\u00ad]/g, '').length;
      while (p.firstChild && (whitespaceNode(p.firstChild) || p.firstChild.nodeName === 'BR')) {
        p.firstChild.remove();
      }
      while (p.lastChild && (whitespaceNode(p.lastChild) || p.lastChild.nodeName === 'BR')) {
        p.lastChild.remove();
      }
    });

    if (blog) {
      this.querySelectorAll('div.n6owBd').forEach((div) => {
        if (blank(div) && !div.querySelector(meaningfulChild)) div.remove();
      });
    }
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  /**
   * The rich text editor puts each inserted image in its own paragraph,
   * so several images added one after another render as a tall stack
   * instead of a side-by-side gallery. Wraps each run of 2+ consecutive
   * "image-only" elements (a paragraph/div whose only content is one
   * <img>) in a flex row.
   */
  #groupConsecutiveImages() {
    /** @type {Element[]} */
    let run = [];

    const flush = () => {
      if (run.length > 1) {
        const wrapper = document.createElement('div');
        wrapper.classList.add('rte-image-group');
        run[0].parentNode?.insertBefore(wrapper, run[0]);
        run.forEach((el) => wrapper.appendChild(el));
      }
      run = [];
    };

    [...this.children].forEach((el) => {
      if (this.#isImageOnly(el)) {
        run.push(el);
      } else {
        flush();
      }
    });
    flush();
  }

  /**
   * @param {Element} el
   * @returns {boolean}
   */
  #isImageOnly(el) {
    if (!(el instanceof HTMLElement)) return false;
    if (el.tagName !== 'P' && el.tagName !== 'DIV') return false;
    const children = [...el.children];
    return children.length === 1 && children[0].tagName === 'IMG' && el.textContent.trim() === '';
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
